/**
 * Background themes for Login and the anonymous public portal — kept in sync so defaults match
 * (Login / Query Page default: light — see loginTheme / portal template in localStorage).
 *
 * Page `background` should read clearly from `panelBg` so split view is not “two white boxes”.
 *
 * @typedef {'light'|'medium'|'dark'|'aqua'|'orchid'} PublicPortalTemplateId
 */
export const LOGIN_PORTAL_THEMES = {
  light: {
    id: 'light',
    label: 'Daylight',
    description: 'Bright open sky with crisp white panels',
    // Soft cyan→sky wash (avoid purple-indigo AI cliché)
    background: 'linear-gradient(165deg, #e0f2fe 0%, #f0f9ff 38%, #ecfeff 72%, #f8fafc 100%)',
    cardBg: 'rgba(255, 255, 255, 0.98)',
    cardBorder: 'rgba(14, 116, 144, 0.18)',
    /** Split layout + job/training cards — solid so MUI `bgcolor` works; distinct from page wash */
    panelBg: '#ffffff',
    panelBorder: 'rgba(14, 116, 144, 0.22)',
    panelShadow: '0 14px 40px rgba(8, 47, 73, 0.08), 0 0 0 1px rgba(255,255,255,0.8) inset',
    textPrimary: '#0c4a6e',
    textSecondary: '#475569',
    decorColor1: 'rgba(14, 165, 233, 0.18)',
    decorColor2: 'rgba(34, 211, 238, 0.14)',
    inputBg: 'rgba(255, 255, 255, 0.98)'
  },
  medium: {
    id: 'medium',
    label: 'Slate',
    description: 'Cool slate frame, readable panels',
    background: 'linear-gradient(155deg, #1e293b 0%, #334155 45%, #0f766e 100%)',
    cardBg: 'rgba(51, 65, 85, 0.94)',
    cardBorder: 'rgba(255, 255, 255, 0.14)',
    panelBg: '#475569',
    panelBorder: 'rgba(148, 163, 184, 0.4)',
    panelShadow: '0 16px 48px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255,255,255,0.07)',
    textPrimary: '#f8fafc',
    textSecondary: '#cbd5e1',
    decorColor1: 'rgba(45, 212, 191, 0.25)',
    decorColor2: 'rgba(129, 140, 248, 0.2)',
    inputBg: 'rgba(255, 255, 255, 0.08)'
  },
  dark: {
    id: 'dark',
    label: 'Midnight',
    description: 'Deep navy with indigo glow',
    background: 'linear-gradient(160deg, #020617 0%, #1e1b4b 38%, #312e81 72%, #0f172a 100%)',
    cardBg: 'rgba(30, 27, 75, 0.9)',
    cardBorder: 'rgba(129, 140, 248, 0.22)',
    panelBg: '#1e293b',
    panelBorder: 'rgba(99, 102, 241, 0.3)',
    panelShadow: '0 20px 50px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
    textPrimary: '#f1f5f9',
    textSecondary: '#94a3b8',
    decorColor1: 'rgba(99, 102, 241, 0.2)',
    decorColor2: 'rgba(236, 72, 153, 0.12)',
    inputBg: 'rgba(255, 255, 255, 0.06)'
  },
  aqua: {
    id: 'aqua',
    label: 'Coast',
    description: 'Teal ocean wash with sea-glass panels',
    background: 'linear-gradient(165deg, #042f2e 0%, #0d9488 40%, #5eead4 88%, #ccfbf1 100%)',
    cardBg: 'rgba(240, 253, 250, 0.95)',
    cardBorder: 'rgba(13, 148, 136, 0.35)',
    panelBg: '#ecfdf5',
    panelBorder: 'rgba(15, 118, 110, 0.38)',
    panelShadow: '0 14px 44px rgba(4, 47, 46, 0.2), inset 0 1px 0 rgba(255,255,255,0.5)',
    textPrimary: '#042f2e',
    textSecondary: '#0f766e',
    decorColor1: 'rgba(45, 212, 191, 0.35)',
    decorColor2: 'rgba(14, 165, 233, 0.2)',
    inputBg: 'rgba(255, 255, 255, 0.9)'
  },
  orchid: {
    id: 'orchid',
    label: 'Aurora',
    description: 'Violet and rose aurora with soft panels',
    background: 'linear-gradient(165deg, #4c1d95 0%, #7c3aed 38%, #db2777 72%, #fda4af 100%)',
    cardBg: 'rgba(255, 255, 255, 0.96)',
    cardBorder: 'rgba(124, 58, 237, 0.28)',
    panelBg: '#faf5ff',
    panelBorder: 'rgba(147, 51, 234, 0.32)',
    panelShadow: '0 14px 44px rgba(76, 29, 149, 0.22), inset 0 1px 0 rgba(255,255,255,0.55)',
    textPrimary: '#1e1b4b',
    textSecondary: '#6b21a8',
    decorColor1: 'rgba(168, 85, 247, 0.3)',
    decorColor2: 'rgba(244, 114, 182, 0.25)',
    inputBg: 'rgba(255, 255, 255, 0.92)'
  }
};

/** Default matches Login page initial theme — bright Daylight for readable Query Page. */
export const LOGIN_PORTAL_DEFAULT_TEMPLATE = 'light';

export const LOGIN_PORTAL_TEMPLATE_IDS = ['light', 'medium', 'dark', 'aqua', 'orchid'];

/** @param {string} id */
export function isValidPortalTemplateId(id) {
  return LOGIN_PORTAL_TEMPLATE_IDS.includes(id);
}
